import { Heart, Award, Users, Globe } from 'lucide-react';
import { motion } from 'framer-motion';

const About = () => {
  return (
    <section id="about" className="py-16 md:py-24 bg-brand-cream overflow-hidden">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="text-center mb-16 md:mb-20"
        >
          <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-gray-900 mb-4 tracking-tight">
            Our Story
          </h2>
          <div className="w-20 md:w-24 h-1.5 bg-gradient-to-r from-brand-rose to-brand-pink mx-auto mb-6 rounded-full"></div>
          <p className="text-base md:text-lg text-gray-600 max-w-3xl mx-auto px-4 leading-relaxed font-medium">
            From London to the world, bringing cultural elegance to contemporary fashion
          </p>
        </motion.div>

        <div className="grid lg:grid-cols-2 gap-16 lg:gap-24 items-center mb-20 md:mb-28">
          {/* Content */}
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="order-2 lg:order-1"
          >
            <div className="space-y-10">
              <div className="group">
                <h3 className="text-xl md:text-2xl font-bold text-gray-900 mb-4 group-hover:text-brand-rose transition-colors duration-300">
                  Founded with Passion
                </h3>
                <p className="text-sm md:text-base text-gray-600 leading-relaxed font-medium">
                  Closetbytejaswi was founded by Bhavya  in May 2024, with a vision to create a fashion label that celebrates individuality, elegance, and modern style. Drawing from rich cultural influences and a passion for design, our brand aims to make fashion accessible, expressive, and meaningful for every customer.
                </p>
              </div>

              <div className="group">
                <h3 className="text-xl md:text-2xl font-bold text-gray-900 mb-4 group-hover:text-brand-rose transition-colors duration-300">
                  London-Based, Globally Inspired
                </h3>
                <p className="text-sm md:text-base text-gray-600 leading-relaxed font-medium">
                  Incorporated in the United Kingdom, we operate as a specialised clothing retailer serving customers both locally and internationally. Our unique design identity blends cultural heritage with contemporary fashion sensibilities, creating pieces that tell stories and inspire confidence.
                </p>
              </div>

              <div className="group">
                <h3 className="text-xl md:text-2xl font-bold text-gray-900 mb-4 group-hover:text-brand-rose transition-colors duration-300">
                  Design Around People
                </h3>
                <p className="text-sm md:text-base text-gray-600 leading-relaxed font-medium">
                  At Closetbytejaswi, every design begins with people. We create outfits inspired by individual personalities, special occasions, and meaningful moments, ensuring each piece feels personal and purposeful. Our approach blends family styling with thoughtful coordination, bringing together looks that feel connected yet unique for everyone.
                </p>
              </div>
            </div>
          </motion.div>

          {/* Image */}
          <motion.div
            initial={{ opacity: 0, x: 30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="relative order-1 lg:order-2 px-4 sm:px-0"
          >
            <div className="relative rounded-3xl overflow-hidden shadow-2xl border-4 border-white">
              <img
                src="/about image.jpg"
                alt="Fashion Boutique"
                className="w-full h-[400px] sm:h-[500px] md:h-[600px] object-cover transition-transform duration-1000 hover:scale-105"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-brand-rose/30 to-transparent"></div>
            </div>

            {/* Floating Cards - Repositioned for Mobile */}
            <motion.div
              initial={{ opacity: 0, scale: 0.8, y: 20 }}
              whileInView={{ opacity: 1, scale: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: 0.4 }}
              className="absolute -bottom-6 right-8 lg:-bottom-8 lg:-left-10 bg-white/90 backdrop-blur-xl rounded-2xl shadow-2xl p-4 md:p-6 max-w-[180px] md:max-w-xs z-10 border border-brand-pink/20 group hover:-translate-y-2 transition-transform duration-300"
            >
              <div className="flex items-center gap-3 md:gap-4">
                <div className="bg-brand-rose/10 p-2.5 md:p-4 rounded-xl shrink-0 group-hover:bg-brand-rose group-hover:text-white transition-colors duration-300">
                  <Award className="w-5 h-5 md:w-8 md:h-8 text-brand-rose group-hover:text-white" />
                </div>
                <div>
                  <div className="font-bold text-xs md:text-lg text-gray-900">Premium Quality</div>
                  <div className="text-[10px] md:text-sm text-gray-500 hidden sm:block font-medium">Finest craftsmanship</div>
                </div>
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, scale: 0.8, y: -20 }}
              whileInView={{ opacity: 1, scale: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: 0.6 }}
              className="absolute -top-6 left-8 lg:-top-8 lg:-right-10 bg-white/90 backdrop-blur-xl rounded-2xl shadow-2xl p-4 md:p-6 max-w-[180px] md:max-w-xs z-10 border border-brand-pink/20 group hover:translate-y-2 transition-transform duration-300"
            >
              <div className="flex items-center gap-3 md:gap-4">
                <div className="bg-brand-pink/10 p-2.5 md:p-4 rounded-xl shrink-0 group-hover:bg-brand-rose group-hover:text-white transition-colors duration-300">
                  <Heart className="w-5 h-5 md:w-8 md:h-8 text-brand-rose group-hover:text-white" />
                </div>
                <div>
                  <div className="font-bold text-xs md:text-lg text-gray-900">Made with Love</div>
                  <div className="text-[10px] md:text-sm text-gray-500 hidden sm:block font-medium">Every piece tells a story</div>
                </div>
              </div>
            </motion.div>
          </motion.div>
        </div>

        {/* Values Grid */}
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-8 md:gap-10">
          <div className="bg-white rounded-3xl p-8 md:p-10 shadow-xl shadow-brand-rose/5 hover:shadow-2xl hover:shadow-brand-rose/10 transition-all duration-500 border border-brand-pink/10 group">
            <div className="bg-brand-cream w-14 h-14 md:w-20 md:h-20 rounded-2xl flex items-center justify-center mb-8 group-hover:bg-brand-rose transition-colors duration-500">
              <Globe className="w-7 h-7 md:w-10 md:h-10 text-brand-rose group-hover:text-white transition-colors duration-500" />
            </div>
            <h3 className="text-xl md:text-2xl font-bold text-gray-900 mb-4 group-hover:text-brand-rose">Global Outlook</h3>
            <p className="text-sm md:text-base text-gray-600 leading-relaxed font-medium">
              London&apos;s Contemporary Fashion. International reach, bringing diverse cultural influences to contemporary fashion.
            </p>
          </div>

          <div className="bg-white rounded-3xl p-8 md:p-10 shadow-xl shadow-brand-rose/5 hover:shadow-2xl hover:shadow-brand-rose/10 transition-all duration-500 border border-brand-pink/10 group">
            <div className="bg-brand-cream w-14 h-14 md:w-20 md:h-20 rounded-2xl flex items-center justify-center mb-8 group-hover:bg-brand-rose transition-colors duration-500">
              <Users className="w-7 h-7 md:w-10 md:h-10 text-brand-rose group-hover:text-white transition-colors duration-500" />
            </div>
            <h3 className="text-xl md:text-2xl font-bold text-gray-900 mb-4 group-hover:text-brand-rose">Customer-Centric</h3>
            <p className="text-sm md:text-base text-gray-600 leading-relaxed font-medium">
              Personalised styling options and dedicated service to help you find the perfect outfit for any occasion.
            </p>
          </div>

          <div className="bg-white rounded-3xl p-8 md:p-10 shadow-xl shadow-brand-rose/5 hover:shadow-2xl hover:shadow-brand-rose/10 transition-all duration-500 border border-brand-pink/10 group sm:col-span-2 lg:col-span-1">
            <div className="bg-brand-cream w-14 h-14 md:w-20 md:h-20 rounded-2xl flex items-center justify-center mb-8 group-hover:bg-brand-rose transition-colors duration-500">
              <Heart className="w-7 h-7 md:w-10 md:h-10 text-brand-rose group-hover:text-white transition-colors duration-500" />
            </div>
            <h3 className="text-xl md:text-2xl font-bold text-gray-900 mb-4 group-hover:text-brand-rose">Quality First</h3>
            <p className="text-sm md:text-base text-gray-600 leading-relaxed font-medium">
              Premium fabrics, exceptional craftsmanship, and attention to detail in every piece we create.
            </p>
          </div>
        </div>
      </div>
    </section>
  );
};

export default About;
